import React, { useEffect, useMemo, useRef, useState } from 'react';
import { IWindowSize, useWindowSize } from '../../../hooks/useWindowSize';
import { getArrayForRange } from '../../../utils/getArrayForRange';
import { Dots, Page } from './pagination.styles';

type UsePaginationType = {
  pageSize: number;
  totalCount: number;
  activePage: number;
  onPageChange: (pageIndex: number) => void;
  relatedParams: any;
};

const DOTS_COMPONENT = 'dots_component';

export const usePagination = ({
  pageSize,
  totalCount,
  activePage,
  onPageChange,
  relatedParams,
}: UsePaginationType) => {
  const paginationRef = useRef<HTMLUListElement>(null);
  const pagesLength = useMemo(
    () => Math.ceil(totalCount / pageSize),
    [pageSize, totalCount]
  );
  const [numberOfVisiblePages, setNumberOfVisiblePages] = useState(0);
  const windowSize: IWindowSize = useWindowSize();
  const timeoutRef = useRef<any>(null);

  useEffect(() => {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      const parentWidth =
        windowSize.width && windowSize.width > 500
          ? (paginationRef?.current as any)?.parentElement.clientWidth
          : window.innerWidth;
      const childWidth = (paginationRef?.current as any)?.children[0]
        .clientWidth;
      const _numberOfVisiblePages =
        Math.floor(parentWidth / (childWidth + 20)) - 2;
      setNumberOfVisiblePages(_numberOfVisiblePages);
    }, 500);
  }, [windowSize]);

  const clickNextPageHandler = () => {
    if (activePage >= pagesLength) {
      return;
    }
    const currentPage = activePage + 1;
    onPageChange(currentPage);
  };

  const clickBackPageHandler = () => {
    if (activePage <= 1) {
      return;
    }
    const currentPage = activePage - 1;
    onPageChange(currentPage);
  };

  const paginationPagesRange = useMemo(() => {
    const isSmall = numberOfVisiblePages <= 3;
    const numberOfElementsIfOneDots = 5;
    const numberOfElementsIfOneDotsSmall = 2;
    const indexOfFirstPage = 1;

    if (numberOfVisiblePages >= pagesLength) {
      return getArrayForRange(1, pagesLength);
    }

    const leftSideElementIndex = Math.max(activePage - 1, indexOfFirstPage);
    const rightSideElementIndex = Math.min(activePage + 1, pagesLength);

    const leftDotsVisible =
      (!isSmall && leftSideElementIndex > 2) ||
      (isSmall && activePage >= pagesLength - 1);
    const rightDotsVisible =
      (!isSmall && rightSideElementIndex < pagesLength - 2) ||
      (isSmall && activePage < pagesLength - 1);

    if (!leftDotsVisible && rightDotsVisible) {
      const indexLastItemRange = !isSmall
        ? numberOfElementsIfOneDots
        : activePage % numberOfElementsIfOneDotsSmall !== 0
        ? activePage + 1
        : activePage;
      const arrayToShow = getArrayForRange(
        isSmall
          ? activePage % numberOfElementsIfOneDotsSmall !== 0
            ? activePage
            : activePage - 1
          : 1,
        indexLastItemRange
      );

      return [...arrayToShow, DOTS_COMPONENT, pagesLength];
    }

    if (leftDotsVisible && !rightDotsVisible) {
      const indexFirstItemRange = !isSmall
        ? numberOfElementsIfOneDots
        : numberOfElementsIfOneDotsSmall;
      const arrayToShow = getArrayForRange(
        pagesLength - indexFirstItemRange + 1,
        pagesLength
      );

      return [
        (!isSmall ||
          (numberOfVisiblePages <= 3 && activePage >= pagesLength - 1)) &&
          indexOfFirstPage,
        DOTS_COMPONENT,
        ...arrayToShow,
      ];
    }

    if (leftDotsVisible && rightDotsVisible) {
      const arrayToShow = getArrayForRange(
        leftSideElementIndex,
        rightSideElementIndex
      );
      return [
        indexOfFirstPage,
        DOTS_COMPONENT,
        ...arrayToShow,
        DOTS_COMPONENT,
        pagesLength,
      ];
    }
  }, [activePage, numberOfVisiblePages, relatedParams]);

  const pages = useMemo(
    () =>
      (paginationPagesRange as any).map((pageNumber: any, index: number) => {
        if (pageNumber === DOTS_COMPONENT) {
          return <Dots key={index + '_dots'}>...</Dots>;
        }

        return (
          <Page
            onClick={() => {
              onPageChange(pageNumber);
            }}
            isActive={pageNumber == activePage}
            key={pageNumber}
          >
            {pageNumber}
          </Page>
        );
      }),
    [paginationPagesRange]
  );

  return {
    paginationRef,
    clickBackPageHandler,
    pages,
    clickNextPageHandler,
    pagesLength,
  };
};
